import * as fs from 'fs';
import * as path from 'path';
import * as os from 'os';
import { input, password } from '@inquirer/prompts';
import chalk from 'chalk';

export interface Config {
  apiKey?: string;
  apiEndpoint?: string;
}

const CONFIG_DIR = path.join(os.homedir(), '.vibetime');
const CONFIG_FILE = path.join(CONFIG_DIR, 'config.json');

// 設定ディレクトリの作成
function ensureConfigDir() {
  if (!fs.existsSync(CONFIG_DIR)) {
    fs.mkdirSync(CONFIG_DIR, { recursive: true });
  }
}

// 設定の読み込み
export function loadConfig(): Config {
  ensureConfigDir();
  
  if (fs.existsSync(CONFIG_FILE)) {
    try {
      const data = fs.readFileSync(CONFIG_FILE, 'utf-8');
      return JSON.parse(data);
    } catch (error) {
      console.error(chalk.yellow('⚠️  Failed to load configuration file'));
      return {};
    }
  }
  
  return {};
}

// 設定の保存
export function saveConfig(config: Config): void {
  ensureConfigDir();
  
  try {
    fs.writeFileSync(CONFIG_FILE, JSON.stringify(config, null, 2));
  } catch (error) {
    console.error(chalk.red('❌ Failed to save configuration file'));
    throw error;
  }
}

// APIキーの設定（対話型）
export async function setupApiKey(forceSetup = false): Promise<string> {
  const config = loadConfig();
  
  // 既にAPIキーが設定されている場合
  if (config.apiKey && !forceSetup) {
    return config.apiKey;
  }
  
  console.log(chalk.cyan('\n🔑 VibeTime API Key Setup\n'));
  
  if (config.apiKey && forceSetup) {
    console.log(chalk.yellow('Current API Key: ') + chalk.gray(`...${config.apiKey.slice(-8)}`));
    console.log(chalk.gray('Enter a new API key or press Enter to keep the current one\n'));
  } else {
    console.log(chalk.gray('You can get your API key from the VibeTime dashboard:'));
    console.log(chalk.blue('https://vibetime.ekusiadadus.workers.dev/dashboard/api-keys\n'));
  }
  
  // API key input
  const apiKey = await password({
    message: 'Enter your API key:',
    mask: '•',
    validate: (value) => {
      if (!value && config.apiKey && forceSetup) {
        return true; // Keep current value if empty
      }
      if (!value || value.trim() === '') {
        return 'API key is required';
      }
      if (!value.startsWith('vbt_')) {
        return 'API key must start with "vbt_"';
      }
      if (value.length < 20) {
        return 'API key is too short';
      }
      return true;
    }
  });
  
  // Update only if new API key was entered
  const finalApiKey = (apiKey && apiKey.trim()) || config.apiKey || '';
  
  // API endpoint configuration (optional)
  const currentEndpoint = config.apiEndpoint || 'https://vibetime.ekusiadadus.workers.dev';
  console.log(chalk.gray(`\nAPI Endpoint (default: ${currentEndpoint})`));
  
  const endpoint = await input({
    message: 'API endpoint:',
    default: currentEndpoint,
  });
  
  // Save configuration
  const newConfig: Config = {
    apiKey: finalApiKey,
    apiEndpoint: endpoint || currentEndpoint,
  };
  
  saveConfig(newConfig);
  
  console.log(chalk.green('\n✅ Configuration saved!\n'));
  console.log(chalk.gray('Configuration file: ') + CONFIG_FILE);
  
  return finalApiKey;
}

// Get API key (show URL and exit if not configured)
export async function getApiKey(options?: { apiKey?: string }): Promise<string> {
  // Command line option takes precedence
  if (options?.apiKey) {
    return options.apiKey;
  }
  
  // Environment variable
  if (process.env.VIBE_API_KEY) {
    return process.env.VIBE_API_KEY;
  }
  
  // Configuration file
  const config = loadConfig();
  if (config.apiKey) {
    return config.apiKey;
  }
  
  // API key not configured - show instructions and exit
  console.log(chalk.red('\n❌ API key not configured'));
  console.log(chalk.gray('An API key is required to use VibeTime.\n'));
  
  console.log(chalk.cyan('📋 How to get your API key:'));
  console.log(chalk.white('1. Visit the VibeTime dashboard:'));
  console.log(chalk.blue('   → https://vibetime.ekusiadadus.workers.dev/dashboard/api-keys'));
  console.log(chalk.white('2. Create a new API key'));
  console.log(chalk.white('3. Configure it using: ') + chalk.green('vibetime config set'));
  console.log(chalk.gray('\nAlternatively, set the VIBE_API_KEY environment variable.'));
  
  throw new Error('API key is required. Please configure it first.');
}

// Get API endpoint
export function getApiEndpoint(): string {
  const config = loadConfig();
  return config.apiEndpoint || process.env.VIBE_API_ENDPOINT || 'https://vibetime.ekusiadadus.workers.dev';
}

// Reset configuration
export function resetConfig(): void {
  if (fs.existsSync(CONFIG_FILE)) {
    fs.unlinkSync(CONFIG_FILE);
    console.log(chalk.green('✅ Configuration reset successfully'));
  } else {
    console.log(chalk.yellow('⚠️  Configuration file not found'));
  }
}